/**
 * CoinVAT Services Module
 * Centralized export for all API services and utilities
 */

// Core API client and utilities
export { apiClient, ApiError } from './api-client';

// Type definitions
export * from './types';

// Service classes and instances
export { AuthService, authService } from './auth';
export { UsersService, usersService } from './users';
export { WalletsService, walletsService } from './wallets';
export { ExchangesService, exchangesService } from './exchanges';
export { WithdrawalsService, withdrawalsService } from './withdrawals';

// Import the service instances for the services object
import { authService } from './auth';
import { usersService } from './users';
import { walletsService } from './wallets';
import { exchangesService } from './exchanges';
import { withdrawalsService } from './withdrawals';
import { ApiError } from './api-client';

// Convenience object for accessing all services
export const services = {
  auth: authService,
  users: usersService,
  wallets: walletsService,
  exchanges: exchangesService,
  withdrawals: withdrawalsService,
};

// Type definitions for the services object
export type Services = typeof services;

// Re-export common types for convenience
export type {
  // Authentication
  UserLogin,
  UserRegistration,
  TokenResponse,
  PasswordChange,
  
  // User Profile
  UserProfile,
  UserProfileUpdate,
  
  // Wallets
  WalletConnection,
  WalletResponse,
  WalletBalanceResponse,
  WalletTransactionResponse,
  SignatureVerificationRequest,
  SignatureVerificationResponse,
  WalletInfo,
  
  // Exchanges
  ExchangeAccountConnection,
  ExchangeAccountResponse,
  ExchangeBalanceResponse,
  ExchangeOrderResponse,
  TradingSessionCreate,
  TradingSessionResponse,
  
  // Withdrawals
  WithdrawalRequest,
  WithdrawalResponse,
  WithdrawalEstimate,
  WithdrawalHistory,
  WithdrawalLimitsResponse,
  WithdrawalVerification,
  TokenInfo,
  WithdrawalHistoryParams,
  WithdrawalStatus,
  
  // Common
  HTTPValidationError,
  ValidationError,
  ApiResponse,
} from './types';

// Service initialization and configuration
export interface ServiceConfig {
  apiBaseUrl?: string;
  enableLogging?: boolean;
  tokenRefreshBuffer?: number;
}

/**
 * Initialize services with custom configuration
 * This is optional - services work with default configuration if not called
 */
export function configureServices(config: ServiceConfig): void {
  // This could be expanded to configure the API client and services
  // For now, services are initialized with defaults
  
  if (config.enableLogging) {
    console.log('CoinVAT Services initialized with config:', config);
  }
}

// Utility functions for common operations

/**
 * Check if user is authenticated across all services
 */
export function isAuthenticated(): boolean {
  return authService.isAuthenticated();
}

/**
 * Ensure valid authentication token
 */
export async function ensureAuthenticated(): Promise<boolean> {
  return authService.ensureValidToken();
}

/**
 * Logout from all services and clear local data
 */
export async function logout(): Promise<void> {
  return authService.logout();
}

/**
 * Get user's display information
 */
export async function getUserDisplayInfo(): Promise<{
  name: string;
  email: string;
  avatarUrl?: string;
  isVerified: boolean;
  isPremium: boolean;
}> {
  const profile = await usersService.getCurrentUser();
  const displayName = await usersService.getDisplayName();
  
  return {
    name: displayName,
    email: profile.email,
    avatarUrl: profile.profile_picture_url || undefined,
    isVerified: profile.is_verified,
    isPremium: profile.is_premium,
  };
}

/**
 * Get comprehensive portfolio overview including withdrawal capabilities
 */
export async function getPortfolioOverview(): Promise<{
  totalValue: number;
  walletValue: number;
  exchangeValue: number;
  walletCount: number;
  exchangeCount: number;
  hasWithdrawalWallet: boolean;
  withdrawalWalletAddress?: string;
}> {
  const [walletValue, exchangeValue, wallets, exchanges, withdrawalWallet] = await Promise.all([
    walletsService.getTotalPortfolioValue().catch(() => 0),
    exchangesService.getTotalExchangePortfolioValue().catch(() => 0),
    walletsService.listWallets().catch(() => []),
    exchangesService.listAccounts().catch(() => []),
    withdrawalsService.getWalletInfo().catch(() => null),
  ]);
  
  return {
    totalValue: walletValue + exchangeValue,
    walletValue,
    exchangeValue,
    walletCount: wallets.length,
    exchangeCount: exchanges.length,
    hasWithdrawalWallet: !!withdrawalWallet,
    withdrawalWalletAddress: withdrawalWallet?.address,
  };
}

/**
 * Get user withdrawal status summary
 */
export async function getWithdrawalSummary(): Promise<{
  hasWithdrawalWallet: boolean;
  walletAddress?: string;
  limits: {
    dailyETH: number;
    dailyUSD: number;
    monthlyETH: number;
    monthlyUSD: number;
  } | null;
  supportedTokensCount: number;
  recentWithdrawalsCount: number;
}> {
  try {
    const [walletInfo, limits, supportedTokens, recentHistory] = await Promise.all([
      withdrawalsService.getWalletInfo().catch(() => null),
      withdrawalsService.getWithdrawalLimits().catch(() => null),
      withdrawalsService.getSupportedTokens().catch(() => []),
      withdrawalsService.getWithdrawalHistory({ limit: 10 }).catch(() => ({ withdrawals: [] } as any)),
    ]);

    return {
      hasWithdrawalWallet: !!walletInfo,
      walletAddress: walletInfo?.address,
      limits: limits ? withdrawalsService.getRemainingLimits(limits) : null,
      supportedTokensCount: supportedTokens.length,
      recentWithdrawalsCount: recentHistory.withdrawals?.length || 0,
    };
  } catch (error) {
    console.error('Failed to get withdrawal summary:', error);
    return {
      hasWithdrawalWallet: false,
      limits: null,
      supportedTokensCount: 0,
      recentWithdrawalsCount: 0,
    };
  }
}

/**
 * Handle API errors consistently across the application
 */
export function handleApiError(error: unknown): {
  message: string;
  status?: number;
  isAuthError: boolean;
} {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      status: error.status,
      isAuthError: error.status === 401 || error.status === 403,
    };
  }
  
  if (error instanceof Error) {
    return {
      message: error.message,
      isAuthError: false,
    };
  }
  
  return {
    message: 'An unexpected error occurred',
    isAuthError: false,
  };
}

// Default export for convenience
export default services;